/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, Fragment, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslate } from "react-admin";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Chip from "@material-ui/core/Chip";
import { useForm } from "react-hook-form";
import {
  Grid,
  Divider,
  Typography,
  Button,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import { TextFieldInForm } from "components/Form";
import ActionMenu from "components/ActionMenu";
import ButtonFooter from "components/ButtonFooter";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import {
  getDepartmentCategoryDetail,
  createDepartmentCategoryDetail,
  updateDepartmentCategoryDetail,
  delDepartmentCategoryDetail,
  getDepartmentCategoryAvailableList,
  assignDepartmentToCategory,
  removeDepartmentFromCategory,
} from "services/setting";
import Model from "models/departmentCategory";
import SnackbarUtils from "utils/SnackbarUtils";

const useStyles = makeStyles((theme) => ({
  content: {
    padding: 4,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  embeddedPanel: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    backgroundColor: "#fff",
  },
  embeddedHeader: {
    padding: theme.spacing(3, 3, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  embeddedHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  embeddedHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  embeddedTitle: {
    fontWeight: 700,
    color: "#1f2a44",
  },
  codeChip: {
    borderRadius: 10,
    fontWeight: 700,
    backgroundColor: "#f3f5fb",
    color: "#546179",
  },
  embeddedSubtitle: {
    color: "#f08a24",
    fontSize: "0.83rem",
    marginTop: theme.spacing(0.75),
  },
  embeddedContent: {
    padding: theme.spacing(1),
    overflow: "auto",
    flex: 1,
  },
  sectionTitle: {
    fontWeight: 700,
    color: "#1f2a44",
    marginBottom: theme.spacing(1),
  },
  statusLabel: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#546179",
    marginRight: theme.spacing(0.5),
  },
  listBoxContainer: {
    display: "flex",
    alignItems: "stretch",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  listBox: {
    flex: 1,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    minHeight: 250,
    maxHeight: 350,
    overflow: "auto",
    padding: theme.spacing(0.5),
    backgroundColor: "#fbfcff",
  },
  listBoxHeader: {
    padding: theme.spacing(1, 1.25),
    backgroundColor: "#f4f6fb",
    fontWeight: "bold",
    fontSize: "0.8rem",
    position: "sticky",
    top: 0,
    zIndex: 1,
    borderRadius: 8,
    textTransform: "uppercase",
    color: "#39445d",
  },
  listBoxItem: {
    padding: theme.spacing(1, 1.25),
    cursor: "pointer",
    borderRadius: 8,
    marginTop: theme.spacing(0.5),
    border: "1px solid #e3e8f4",
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&.selected": {
      backgroundColor: "#e8ecff",
      color: "#243665",
      fontWeight: 600,
      borderColor: "#b9c4ff",
    },
  },
  memberItem: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  memberCode: {
    color: "#4256d0",
    fontWeight: 700,
    fontSize: "0.82rem",
  },
  memberDesc: {
    color: "#1f2a44",
    fontSize: "0.9rem",
  },
  countBadge: {
    minWidth: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "#ffffff",
    color: "#546179",
    fontWeight: 700,
    fontSize: "0.78rem",
  },
  moveButtonsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: theme.spacing(0.5),
    paddingInline: theme.spacing(0.5),
  },
  moveButton: {
    minWidth: 44,
    width: 44,
    height: 44,
    borderRadius: 22,
    boxShadow: "none",
  },
  removeButton: {
    minWidth: 28,
    width: 28,
    height: 28,
    color: "#ff6b81",
  },
  footerActions: {
    paddingTop: theme.spacing(2),
  },
}));

const DialogTitle = (props) => {
  const { children, onClose, ...other } = props;
  const classes = useStyles();
  return (
    <MuiDialogTitle disableTypography {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
};

const DialogItem = (props) => {
  const translate = useTranslate();
  const classes = useStyles();
  const { id, mode, setMode, open, onClose, embedded = false } = props;
  const [isBtnLoading, setBtnLoading] = useState(false);
  const [data, setData] = useState(Model);
  const [availableDepts, setAvailableDepts] = useState([]);
  const [assignedDepts, setAssignedDepts] = useState([]);
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [selectedAssigned, setSelectedAssigned] = useState([]);
  const mountedRef = useRef(true);
  const methods = useForm({ defaultValues: data });

  const { handleSubmit, reset, watch, setValue } = methods;
  const isActive = watch("IsActive");
  const watchedCategoryCode = watch("CategoryCode");
  const watchedCategoryName = watch("CategoryName");

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadData = useCallback(async () => {
    setBtnLoading(true);
    const availableResponse = await getDepartmentCategoryAvailableList();
    if (!mountedRef.current) return;
    const rawAvailable = availableResponse?.Data || [];

    if (id && id !== 0) {
      const response = await getDepartmentCategoryDetail(id);
      if (response && mountedRef.current) {
        const assigned = response.Departments || [];
        const nextAvailable = rawAvailable.filter(
          (dept) => !assigned.some((item) => Number(item.Id) === Number(dept.Id)),
        );

        setData(response);
        reset(response);
        setAssignedDepts(assigned);
        setAvailableDepts(nextAvailable);
      }
    } else {
      setData(Model);
      reset(Model);
      setAssignedDepts([]);
      setAvailableDepts(rawAvailable);
    }

    setSelectedAvailable([]);
    setSelectedAssigned([]);
    if (mountedRef.current) {
      setBtnLoading(false);
    }
  }, [id, reset]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, loadData]);

  const disableFormEnter = (e) => {
    if (e.key === "Enter" && e.target.localName !== "textarea") {
      e.preventDefault();
    }
  };

  const handleClose = (value) => {
    onClose(value);
  };

  const Save = async (values) => {
    if (mode === "edit") {
      const payload = { ...values, Id: id };
      const { Code, InternalMessage, UserMessage } =
        await updateDepartmentCategoryDetail(payload);
      if (Code === 0) {
        SnackbarUtils.success(UserMessage);
        handleClose(id);
      } else if (InternalMessage) {
        SnackbarUtils.error(InternalMessage);
      } else {
        SnackbarUtils.warning(UserMessage);
      }
      if (mountedRef.current) {
        setBtnLoading(false);
      }
      return;
    }

    const { Code, InternalMessage, UserMessage } =
      await createDepartmentCategoryDetail(values);
    if (Code === 0) {
      SnackbarUtils.success(UserMessage);
      handleClose(parseInt(InternalMessage, 10));
    } else if (InternalMessage) {
      SnackbarUtils.error(InternalMessage);
    } else {
      SnackbarUtils.warning(UserMessage);
    }
    if (mountedRef.current) {
      setBtnLoading(false);
    }
  };

  const onSubmit = (values) => {
    setBtnLoading(true);
    const nextValues = {
      ...data,
      ...values,
      UserModified: Model.UserModified,
    };
    setData(nextValues);
    Save(nextValues);
  };

  const DelOrVoid = async (itemId) => {
    let msg = translate("ra.question.confirmDel");
    SnackbarUtils.delConfirm(msg, async function () {
      const { Code, UserMessage } = await delDepartmentCategoryDetail(itemId);
      if (Code === 0) {
        SnackbarUtils.success(UserMessage);
        handleClose(itemId);
      }
    });
  };

  const CancelFnc = () => {
    if (id === 0) {
      onClose();
      return;
    }
    loadData();
    setMode("view");
  };

  const handleAssignDepartments = async () => {
    if (selectedAvailable.length === 0) return;
    const deptIds = selectedAvailable.map((item) => item.Id);
    const { Code, UserMessage } = await assignDepartmentToCategory({
      CategoryId: id,
      DepartmentIds: deptIds,
    });
    if (Code === 0) {
      SnackbarUtils.success(UserMessage);
      const moved = availableDepts.filter((item) => selectedAvailable.includes(item));
      if (mountedRef.current) {
        setAssignedDepts((prev) => [...prev, ...moved]);
        setAvailableDepts((prev) =>
          prev.filter((item) => !selectedAvailable.includes(item)),
        );
        setSelectedAvailable([]);
      }
    } else {
      SnackbarUtils.warning(UserMessage);
    }
  };

  const toggleAvailableSelection = (dept) => {
    setSelectedAvailable((prev) =>
      prev.includes(dept) ? prev.filter((item) => item !== dept) : [...prev, dept],
    );
  };

  const toggleAssignedSelection = (dept) => {
    setSelectedAssigned((prev) =>
      prev.includes(dept) ? prev.filter((item) => item !== dept) : [...prev, dept],
    );
  };

  const menuControlProp = [
    {
      name: "Edit",
      fnc: () => setMode("edit"),
      disabled: mode !== "view" || id === 0,
    },
    {
      name: "Delete",
      fnc: () => DelOrVoid(id),
      disabled: mode !== "view" || id === 0,
    },
  ];

  const panelTitle =
    mode === "add"
      ? translate("ra.field.Create New Group")
      : watchedCategoryName || props.title;

  const handleRemoveSingleDepartment = async (dept) => {
    if (mode !== "edit" || !dept?.Id) return;
    const { Code, UserMessage } = await removeDepartmentFromCategory({
      CategoryId: id,
      DepartmentIds: [dept.Id],
    });
    if (Code === 0) {
      SnackbarUtils.success(UserMessage);
      if (mountedRef.current) {
        setAssignedDepts((prev) =>
          prev.filter((item) => Number(item.Id) !== Number(dept.Id)),
        );
        setAvailableDepts((prev) => [...prev, dept]);
        setSelectedAssigned((prev) =>
          prev.filter((item) => Number(item.Id) !== Number(dept.Id)),
        );
      }
    } else {
      SnackbarUtils.warning(UserMessage);
    }
  };

  const formContent = (
    <Fragment>
      {mode === "view" && id !== 0 ? (
        <ActionMenu menuControl={menuControlProp} justify="flex-end" size="auto" />
      ) : null}
      {mode === "view" && id !== 0 ? <Divider style={{ marginBottom: 16 }} /> : null}

      <form onKeyDown={disableFormEnter}>
        {id === 0 ? (
          <Grid container justifyContent="flex-start" spacing={2}>
            <Grid item xs={12} md={6}>
              <TextFieldInForm
                label={`* ${translate("ra.field.Category Code")}`}
                name="CategoryCode"
                variant="outlined"
                margin="dense"
                methods={methods}
                disabled={mode !== "add"}
                rule={{
                  required: {
                    value: true,
                    message: translate("ra.validation.required"),
                  },
                  maxLength: {
                    value: 20,
                    message: "maximum length is 20",
                  },
                }}
                autoFocus
              />
              {mode === "add" ? (
                <Typography className={classes.embeddedSubtitle}>
                  {translate("ra.field.This code cannot be changed once saved.")}
                </Typography>
              ) : null}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextFieldInForm
                label={`* ${translate("ra.field.Category Name")}`}
                name="CategoryName"
                variant="outlined"
                margin="dense"
                methods={methods}
                disabled={mode === "view"}
                rule={{
                  required: {
                    value: true,
                    message: translate("ra.validation.required"),
                  },
                  maxLength: {
                    value: 255,
                    message: "maximum length is 255",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextFieldInForm
                label={translate("ra.field.Description")}
                name="Description"
                variant="outlined"
                margin="dense"
                methods={methods}
                disabled={mode === "view"}
                rule={{
                  maxLength: {
                    value: 500,
                    message: "maximum length is 500",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive ?? true}
                    onChange={(e) => setValue("IsActive", e.target.checked)}
                    color="primary"
                    disabled={mode === "view"}
                  />
                }
                label={translate("ra.field.Active")}
              />
            </Grid>
          </Grid>
        ) : (
          <div style={{ marginTop: 24 }}>
            <div className={classes.listBoxContainer}>
              <div className={classes.listBox}>
                <div className={classes.listBoxHeader}>
                  <Grid container justifyContent="space-between" alignItems="center">
                    <span>{translate("ra.field.Available Departments")}</span>
                    <Chip
                      label={availableDepts.length}
                      size="small"
                      className={classes.countBadge}
                    />
                  </Grid>
                </div>
                {availableDepts.map((dept) => (
                  <div
                    key={dept.Id}
                    className={`${classes.listBoxItem} ${
                      selectedAvailable.includes(dept) ? "selected" : ""
                    }`}
                    onClick={() => toggleAvailableSelection(dept)}
                  >
                    {dept.DeptCode} - {dept.Description}
                  </div>
                ))}
                {availableDepts.length === 0 && (
                  <div style={{ padding: 16, textAlign: "center", color: "#999" }}>
                    {translate("ra.info.notransaction")}
                  </div>
                )}
              </div>

              <div className={classes.moveButtonsContainer}>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  className={classes.moveButton}
                  disabled={selectedAvailable.length === 0 || mode === "view"}
                  onClick={handleAssignDepartments}
                >
                  <ArrowForwardIcon />
                </Button>
              </div>

              <div className={classes.listBox}>
                <div className={classes.listBoxHeader}>
                  <Grid container justifyContent="space-between" alignItems="center">
                    <span>{translate("ra.field.Group Members")}</span>
                    <Chip
                      label={assignedDepts.length}
                      size="small"
                      className={classes.countBadge}
                    />
                  </Grid>
                </div>
                {assignedDepts.map((dept) => (
                  <div
                    key={dept.Id}
                    className={`${classes.listBoxItem} ${
                      selectedAssigned.includes(dept) ? "selected" : ""
                    }`}
                    onClick={() => toggleAssignedSelection(dept)}
                  >
                    <div className={classes.memberItem}>
                      <div>
                        <div className={classes.memberCode}>{dept.DeptCode}</div>
                        <div className={classes.memberDesc}>{dept.Description}</div>
                      </div>
                      {mode === "edit" ? (
                        <IconButton
                          size="small"
                          className={classes.removeButton}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemoveSingleDepartment(dept);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </div>
                  </div>
                ))}
                {assignedDepts.length === 0 && (
                  <div style={{ padding: 16, textAlign: "center", color: "#999" }}>
                    {translate("ra.info.notransaction")}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {process.env.NODE_ENV === "development" ? (
          <pre>{JSON.stringify(data, 0, 2)}</pre>
        ) : null}

        {mode !== "view" ? (
          <div className={classes.footerActions}>
            <ButtonFooter
              disabled={isBtnLoading}
              SaveFnc={handleSubmit(onSubmit)}
              CancelFnc={CancelFnc}
              saveTitle={translate("ra.field.Save Group")}
            />
          </div>
        ) : null}
      </form>
    </Fragment>
  );

  if (embedded) {
    return (
      <div className={classes.embeddedPanel}>
        <div className={classes.embeddedHeader}>
          {id === 0 ? (
            <Typography variant="h5" className={classes.embeddedTitle}>
              {panelTitle}
            </Typography>
          ) : (
            <div className={classes.embeddedHeaderRow}>
              <div className={classes.embeddedHeaderLeft}>
                <Typography variant="h5" className={classes.embeddedTitle}>
                  {panelTitle}
                </Typography>
                <Chip
                  label={watchedCategoryCode || "-"}
                  size="small"
                  className={classes.codeChip}
                />
              </div>
              <div>
                <span className={classes.statusLabel}>
                  {translate("ra.field.Active").toUpperCase()}
                </span>
                <Switch
                  checked={isActive ?? true}
                  onChange={(e) => setValue("IsActive", e.target.checked)}
                  color="primary"
                  disabled={mode !== "edit"}
                />
              </div>
            </div>
          )}
        </div>
        <div className={classes.embeddedContent}>{formContent}</div>
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose(event);
        }
      }}
      scroll="paper"
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="scroll-dialog-title" onClose={onClose}>
        {props.title}
      </DialogTitle>
      <DialogContent dividers className={classes.content}>
        {formContent}
      </DialogContent>
    </Dialog>
  );
};

export default DialogItem;
